import { yupResolver } from "@hookform/resolvers/yup"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"
import { VideoSquare } from "iconsax-reactjs"
import { AlertCircle, Loader2, Upload, X } from "lucide-react"
import React, { useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { toast } from "sonner"

import { BottomSheetModal, MessageError } from "@/components/shared"
import { Alert, AlertDescription, Button, Card, Input, Label, Textarea } from "@/components/ui"
import {
  getDraftsByPotentialId,
  getLinkS3UploadDraft,
  submitDraft,
  submitLinkDraftTAP,
  updateCodeDraftTAP
} from "@/features/jobs/manage/api/draft.api"
import { DraftDetail } from "@/features/jobs/manage/components/draft"
import { ECampaignType, EDraftStatus, EMediaType, EValidateDraft } from "@/modules/helpers/enum"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { REASON_REJECT_ACCEPTANCE_MESSAGE, TEXT_STATUS_DRAFT } from "@/modules/helpers/options"
import { schemaCommentDraft } from "@/modules/helpers/schemas"
import { sanitizeFileName, uploadMultipleFilesToS3 } from "@/modules/helpers/utils/aws-s3"
import type { Draft, DraftGallery, Potential } from "@/modules/types"

interface StandardGuideProps {
  potentials?: Partial<Potential>[]
  campaignType: ECampaignType
  submissionId?: string
  demoIndex: number
  list: any[]
  workingAt: string
  disabled?: boolean
  refetch: () => void
  color?: string
}

export const StandardGuide: React.FC<StandardGuideProps> = ({
  campaignType,
  demoIndex,
  potentials,
  submissionId,
  list,
  disabled,
  workingAt,
  refetch
}) => {
  const form = useForm({
    defaultValues: {
      submitUrl: "",
      codeAds: ""
    }
  })

  const { mutate: updateDraftInfo, isPending } = useMutation({
    mutationKey: ["update_draft_info_" + potentials?.[0]?.id],
    mutationFn: async (data: { link?: string; codeAds?: string; draftId: string }) => {
      const promises = []

      if (data.link !== undefined) {
        promises.push(submitLinkDraftTAP(data.draftId, data.link))
      }

      if (data.codeAds !== undefined) {
        promises.push(updateCodeDraftTAP(data.draftId, data.codeAds))
      }

      return await Promise.all(promises)
    },
    onSuccess(_data, variables) {
      refetch()
      const messages = []
      if (variables.link !== undefined) messages.push("đường dẫn")
      if (variables.codeAds !== undefined) messages.push("code quảng cáo")
      toast.success(`Cập nhật ${messages.join(" và ")} thành công`)
    },
    onError(error: any, variables) {
      // Only handle submit URL errors if link was updated
      if (variables.link !== undefined) {
        const type = error?.response?.data?.error?.type
        if (type?.includes(EValidateDraft.LACK_OF_PERMISSION)) {
          form.setError("submitUrl", {
            message:
              "Zizi Project không thể kiểm tra thông tin kênh TikTok của bạn. Vui lòng đăng nhập lại và cấp thêm quyền."
          })
          return
        }
        if (
          type?.includes(EValidateDraft.VIDEO_IS_NOT_OWNED) ||
          type?.includes(EValidateDraft.VIDEO_SUBMIT_NOT_FOUND)
        ) {
          form.setError("submitUrl", {
            message: "Kênh TikTok của bạn không sở hữu video này."
          })
          return
        }
        if (type?.includes(EValidateDraft.USER_NOT_LINKING_TIKTOK_YET)) {
          form.setError("submitUrl", {
            message: "Bạn cần kết nối kênh TikTok."
          })
          return
        }
        if (type?.includes(EValidateDraft.DEMO_LINK_ALREADY_EXISTED)) {
          form.setError("submitUrl", {
            message: "Đường dẫn bài đăng mới không được trùng với đường dẫn bài đăng cũ trước đó"
          })
          return
        }
        if (type?.includes(EValidateDraft.INVALID_CODE_ADS)) {
          form.setError("codeAds", {
            message: "Mã quảng cáo không hợp lệ"
          })
          return
        }
        form.setError("submitUrl", { message: "Xảy ra lỗi. Vui lòng thử lại sau!" })
      } else {
        toast.error("Cập nhật thất bại. Vui lòng thử lại!")
      }
    }
  })

  const lastDraftReviewed =
    !list?.length ||
    list?.[list?.length - 1]?.status === EDraftStatus.REVIEWED ||
    list?.[list?.length - 1]?.status === EDraftStatus.REJECTED
  const draftOnAir = list?.find((item) =>
    [
      EDraftStatus.FINALIZED,
      EDraftStatus.ACCEPTED_ACCEPTANCE,
      EDraftStatus.REJECTED_ACCEPTANCE
    ].includes(item?.status)
  )

  useEffect(() => {
    form.setValue("codeAds", draftOnAir?.codeAds || "")
    form.setValue("submitUrl", draftOnAir?.submitUrl || "")
  }, [draftOnAir?.codeAds, draftOnAir?.submitUrl, form])

  return (
    <div className="mx-4 flex flex-col rounded-xl p-4 mb-3 transition-transform bg-background">
      {list
        ?.filter((item) => !!item?.id)
        ?.map((item, i) => (
          <div key={item?.id + item?.status} className="relative">
            <span className="flex items-center gap-1 text-xs font-medium text-black">
              <VideoSquare size={16} color="#000" variant="Outline" /> Video {demoIndex}.{i}
            </span>
            <DraftSocialManageItem
              draft={item}
              potential={potentials?.[0]}
              campaignType={campaignType}
              submissionId={submissionId}
              dueDate={workingAt}
              version={`${demoIndex}.${i}`}
              refetch={refetch}
            />
          </div>
        ))}
      {lastDraftReviewed && (
        <div className="relative">
          <span className="flex items-center gap-1 text-xs font-medium text-black">
            <VideoSquare size={16} color="#000" variant="Outline" /> Video {demoIndex}.
            {list?.length}
          </span>
          <DraftSocialManageItem
            disabled={disabled}
            potential={potentials?.[0]}
            dueDate={workingAt}
            campaignType={campaignType}
            submissionId={submissionId}
            version={`${demoIndex}.${list?.length}`}
            refetch={refetch}
          />
        </div>
      )}

      {!!draftOnAir && (
        <div className="flex flex-col gap-2">
          {draftOnAir?.reason && draftOnAir?.status === EDraftStatus.REJECTED_ACCEPTANCE ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex gap-2 items-start">
                <span className="text-sm text-red-600 min-w-fit">Lý do từ chối nghiệm thu:</span>
                <span className="text-sm text-red-600 font-medium">
                  {draftOnAir.reason ? REASON_REJECT_ACCEPTANCE_MESSAGE[draftOnAir.reason] : ""}
                </span>
              </div>
            </div>
          ) : null}
          <p className="text-xs text-black ">
            Hãy nhập link bài đăng TikTok của demo được Nhãn hàng chấp nhận vào ô bên dưới
          </p>
          <form
            onSubmit={form.handleSubmit(({ submitUrl, codeAds }) => {
              const updates: { link?: string; codeAds?: string; draftId: string } = {
                draftId: draftOnAir?.id
              }

              if (submitUrl !== draftOnAir?.submitUrl) {
                updates.link = submitUrl
              }
              if (codeAds !== draftOnAir?.codeAds) {
                updates.codeAds = codeAds
              }

              // Only call if there are updates
              if (updates.link !== undefined || updates.codeAds !== undefined) {
                updateDraftInfo(updates)
              }
            })}
            className="flex flex-col gap-3 mt-2 w-full"
          >
            {/* Input submitUrl */}
            <div className="flex flex-col gap-1">
              <Label htmlFor="submitUrl" className="text-black text-xs">
                Đường dẫn bài đăng <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="submitUrl"
                control={form.control}
                render={({ field, fieldState }) => (
                  <>
                    <Input
                      id="submitUrl"
                      placeholder="Nhập đường dẫn bài đăng TikTok"
                      disabled={isPending}
                      {...field}
                      className={cn(
                        "h-10 rounded-lg text-xs",
                        fieldState.error && "border-red-500"
                      )}
                    />
                    {fieldState.error && (
                      <Alert className="p-2">
                        <AlertDescription className="text-xs text-red-500 flex gap-2 items-center">
                          <AlertCircle className="w-4 h-4" /> {fieldState.error.message}
                        </AlertDescription>
                      </Alert>
                    )}
                  </>
                )}
              />
            </div>

            {/* Input codeAds */}
            <div className="flex flex-col gap-1">
              <Label htmlFor="codeAds" className="text-black text-xs">
                Mã quảng cáo
              </Label>
              <Controller
                name="codeAds"
                control={form.control}
                render={({ field, fieldState }) => (
                  <>
                    <Input
                      id="codeAds"
                      placeholder="Nhập mã quảng cáo"
                      {...field}
                      className={cn(
                        "h-10 rounded-lg text-xs",
                        list[0]?.codeAds && "bg-gray-100",
                        fieldState.error && "border-red-500"
                      )}
                    />
                    {fieldState.error && (
                      <Alert className="p-2">
                        <AlertDescription className="text-xs text-red-500 flex gap-2 items-center">
                          <AlertCircle className="w-4 h-4" /> {fieldState.error.message}
                        </AlertDescription>
                      </Alert>
                    )}
                  </>
                )}
              />
            </div>

            <Button
              type="submit"
              size="sm"
              disabled={
                isPending ||
                (form.watch("submitUrl") === draftOnAir?.submitUrl &&
                  form.watch("codeAds") === draftOnAir?.codeAds) ||
                draftOnAir.status === EDraftStatus.ACCEPTED_ACCEPTANCE
              }
              className="rounded-full w-24 self-end text-xs"
            >
              {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {list[0]?.submitUrl ? "Cập nhật" : "Gửi"}
            </Button>
          </form>
        </div>
      )}
    </div>
  )
}

interface DraftItemProps {
  status?: EDraftStatus
  potential?: Partial<Potential>
  disabled?: boolean
  submissionId?: string
  campaignType?: ECampaignType
  data?: Draft
  onOpenUpload?: () => void
}

import { generateUniqueId } from "@/modules/helpers/hooks/shared/utils"

interface DraftForm {
  description: string
  sources: { file: File; id: string }[]
  error?: string
}

const ActionDraft: React.FC<DraftItemProps> = ({ data, disabled, onOpenUpload }) => {
  const onPress = () => {
    onOpenUpload?.()
  }

  return (
    <Button
      variant="default"
      size="sm"
      // disabled={disabled}
      className={cn(
        "!h-10 w-fit rounded-xl !bg-primary !text-white !text-xs py-0 px-6 font-normal",
        !data?.id && "bg-accent text-black hover:bg-accent/90",
        disabled && "border-gray-300 opacity-40 "
      )}
      onClick={onPress}
    >
      Đăng tải
    </Button>
  )
}

interface DraftSocialManageItemProps {
  potential?: Partial<Potential>
  submissionId?: string
  disabled?: boolean
  draft?: Draft
  campaignType?: ECampaignType
  dueDate?: string
  version: string
  refetch?: () => void
}

export const DraftSocialManageItem: React.FC<DraftSocialManageItemProps> = ({
  draft,
  potential,
  disabled,
  submissionId,
  dueDate,
  campaignType,
  version,
  refetch
}) => {
  const queryClient = useQueryClient()
  const [openUpload, setOpenUpload] = useState(false)
  const [progress, setProgress] = useState(0)
  const [openConfirm, setOpenConfirm] = useState(false)

  const form = useForm<DraftForm>({
    defaultValues: {
      description: "",
      sources: [],
      error: ""
    },
    resolver: yupResolver(schemaCommentDraft) as any
  })

  const sources = form.watch("sources")
  const error = form.watch("error")

  const { mutate, isPending } = useMutation({
    mutationFn: (data: { description: string; draftGalleries: DraftGallery[]; version: string }) =>
      uploadAssets(data.draftGalleries, data.version),
    onSuccess(_, raw) {
      if (potential?.id) {
        const versionNumber = Number(raw.version)
        return submitDraft({
          potentialId: potential.id,
          draftGalleries: raw.draftGalleries,
          content: raw.description,
          campaignType: ECampaignType.AFFILIATE,
          version: versionNumber
        }).then(() => {
          form.setValue("sources", [])
          form.reset({})
          queryClient.invalidateQueries({
            queryKey: [`drafts_of_potential_${potential.id}`]
          })
          localStorage.removeItem("@potentialCurrent")
          setOpenUpload(false)
          setOpenConfirm(false)
          refetch?.()
          toast.success("Đăng tải bản nháp thành công")
        })
      }
    },
    onError(error: any) {
      form.setValue("error", error?.message || "Có lỗi xảy ra!")
      toast.error(error?.message || "Có lỗi xảy ra!")
    }
  })

  const uploadAssets = async (draftGalleries: DraftGallery[], version?: string): Promise<any> => {
    if (!potential?.id || !submissionId) {
      throw { message: "Không tìm thấy định dạng bài đăng." }
    }

    if (!version) {
      throw { message: "Không tìm thấy phiên bản." }
    }

    const draft = await getDraftsByPotentialId(potential.id)
    const drafts = draft?.data?.data?.list

    const versionNumber = version
    const currentVersion = drafts.find((item) => item.version.toString() === versionNumber)
    if (currentVersion) {
      throw {
        message: `Bản nháp ${version} đã được đăng tải trước đó.`
      }
    }

    // Get presigned URLs from API
    const urls = await getLinkS3UploadDraft(draftGalleries)
    const linkUploadGalleries = urls?.data?.data

    if (!linkUploadGalleries || !Array.isArray(linkUploadGalleries)) {
      throw { message: "Không thấy bản dữ liệu" }
    }

    // Map files with URLs
    const uploads = linkUploadGalleries.map((url, index) => ({
      url,
      file: sources[index]?.file || null
    }))

    // Upload all files with progress tracking
    return await uploadMultipleFilesToS3(uploads, (progress) => {
      setProgress(progress)
    })
  }

  const onUpload = ({ description }: { description: string }) => {
    const draftGalleries: DraftGallery[] = sources.map((item, index) => {
      const sanitizedName = sanitizeFileName(item.file.name)
      return {
        name: `${version}_${index}_${potential?.id}_${sanitizedName}`,
        type: item.file.type.startsWith("image") ? EMediaType.IMAGE : EMediaType.VIDEO
      }
    })
    mutate({ description, draftGalleries, version })
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const files = e.target.files
      if (files && files.length > 0) {
        const validFiles = Array.from(files).filter((file) => file.type.startsWith("video/"))
        const filesWithIds = validFiles.map((file) => ({
          file,
          id: generateUniqueId()
        }))
        form.setValue("sources", [...sources, ...filesWithIds])
        form.setValue("error", "")
      } else {
        form.setValue("error", "Bản nháp phải là video")
      }
    } catch {
      form.setValue("error", "Bản nháp phải là video")
    }
  }

  const handleCheck = () => {
    if (!submissionId) {
      form.setValue("error", "Không tìm thấy đích bản nháp.")
      return
    }
    if (!sources.length) {
      form.setValue("error", "Lỗi chưa tải bản nháp.")
      return
    }
    setOpenConfirm(true)
  }

  const handleOpenUpload = () => {
    setOpenUpload(true)
    form.reset({
      description: "",
      sources: [],
      error: ""
    })
  }

  if (draft?.id) {
    return (
      <div className="mt-2">
        <DraftDetail id={draft?.id} />
      </div>
    )
  }

  return (
    <>
      <Card className="mt-5 rounded-xl border border-gray-300 border-dashed p-4 gap-2 bg-white">
        <div className="flex items-center justify-between">
          {campaignType === ECampaignType.REVIEW_PRODUCT && (
            <p className="text-xs font-medium">
              Hạn đăng tải bản nháp:{" "}
              <span className="font-semibold">
                {dayjs(dueDate).add(14, "day").format("DD/MM/YYYY")}
              </span>
            </p>
          )}

          <div
            className={cn(
              "rounded-full px-2 py-1 border text-[10px] font-semibold",
              disabled && "opacity-40",
              draft?.status &&
                [EDraftStatus.WAITING, EDraftStatus.DRAFT].includes(draft?.status) &&
                "border-gray-600 text-gray-400",
              draft?.status &&
                [
                  EDraftStatus.FINALIZED,
                  EDraftStatus.APPROVED,
                  EDraftStatus.ACCEPTED_ACCEPTANCE
                ].includes(draft?.status) &&
                "border-green-500 text-green-500",
              draft?.status &&
                [EDraftStatus.REJECTED, EDraftStatus.REJECTED_ACCEPTANCE].includes(draft?.status) &&
                "border-red-500 text-red-500"
            )}
          >
            {draft?.status ? TEXT_STATUS_DRAFT[draft?.status] : "Chờ đăng"}
          </div>
        </div>

        <div className="w-full flex justify-center items-center">
          {!draft?.id && (
            <ActionDraft
              potential={potential}
              data={draft}
              disabled={disabled}
              campaignType={campaignType}
              submissionId={submissionId}
              onOpenUpload={handleOpenUpload}
            />
          )}
        </div>
      </Card>

      <BottomSheetModal
        isVisible={openUpload}
        setIsVisible={setOpenUpload}
        title={`Đăng tải bản nháp ${version}`}
      >
        <div className={cn("flex flex-col gap-4 px-4")}>
          <Controller
            name="description"
            control={form.control}
            render={({ field, fieldState }) => (
              <MessageError error={fieldState.error?.message}>
                <p className="text-sm font-medium">Mô tả nội dung</p>
                <Textarea
                  {...field}
                  placeholder="Nhập mô tả..."
                  className="mt-2 text-sm rounded-xl"
                />
              </MessageError>
            )}
          />
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="border rounded-xl p-4">
            <div className="flex flex-wrap gap-4 justify-center">
              {sources.map((item) => {
                const videoUrl = URL.createObjectURL(item.file)
                return (
                  <div
                    key={item.id}
                    className="relative w-32 h-32 border rounded-lg overflow-hidden"
                  >
                    <video
                      src={videoUrl}
                      className="object-cover w-full h-full"
                      preload="metadata"
                      muted
                      playsInline
                      onLoadedMetadata={(e) => {
                        const video = e.target as HTMLVideoElement
                        video.currentTime = 0.1
                      }}
                    />
                    <button
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full"
                      onClick={() => {
                        URL.revokeObjectURL(videoUrl)
                        form.setValue(
                          "sources",
                          sources.filter((f) => f.id !== item.id)
                        )
                      }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                )
              })}
            </div>
            {sources?.length === 0 && (
              <div className="flex justify-center mt-2">
                <label className="flex flex-col items-center justify-center w-32 h-32 rounded-xl border-2 border-dashed border-primary bg-gray-50 text-gray-500 cursor-pointer">
                  <Upload size={24} className="mb-2 text-primary" />
                  <p className="text-xs font-normal text-primary">Tải lên video</p>
                  <input
                    type="file"
                    accept="video/*"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
              </div>
            )}
          </div>

          <Button
            className="w-full text-sm h-12 bg-primary-800 rounded-xl mb-2"
            disabled={isPending}
            onClick={form.handleSubmit(handleCheck)}
          >
            Gửi
          </Button>
        </div>
      </BottomSheetModal>

      <BottomSheetModal isVisible={openConfirm} setIsVisible={setOpenConfirm} title="Xác nhận">
        <div className={cn("flex flex-col gap-2 px-2")}>
          <p className="text-sm font-medium px-2">
            Thao tác này không thể chỉnh sửa. Hãy kiểm tra trước khi bấm xác nhận.
          </p>
          {isPending && (
            <div className="flex items-center justify-center">
              <p className="flex items-center gap-1 font-semibold text-sm">
                Đang tải lên
                <span className="text-sm font-semibold">{Math.floor(progress)}%</span>
              </p>
            </div>
          )}
          <Button
            className="w-full text-sm !h-12 rounded-xl bg-primary-800 mb-2"
            onClick={form.handleSubmit((data) => onUpload(data))}
            disabled={isPending}
          >
            Xác nhận tải lên
          </Button>
        </div>
      </BottomSheetModal>
    </>
  )
}
